import type {
  Company,
  Contact,
  DailyLog,
  DateOnly,
  File,
  Folder,
  Job,
  Member,
  Membership,
  Role,
  Allowance,
  ChangeOrder,
  ScheduleColour,
  ScheduleItem,
  SchedulePhase,
  StockMovement,
  Selection,
  Tag,
  TaxRate,
  Todo,
} from '@buildmate/core';
import {
  addWorkingDays,
  BUILT_IN_ROLES,
  DEFAULT_PORTAL_MODULES,
  MODULES,
  DEFAULT_WORK_WEEK,
  folderKindFor,
  isOwnerVisible,
  jobCalendar,
  nextWorkingDay,
  ownerChangeOrderFrom,
  markupFromPercent,
  type CostCode,
  type CostCodeCategory,
  type CostType,
  type CatalogAssembly,
  type CatalogItem,
  money,
} from '@buildmate/core';

/**
 * One company, three users and one job.
 *
 * The same fixtures back unit tests, Storybook and the emulator seed, so a bug
 * found by hand is reproducible in a test without rebuilding the scenario.
 * Identifiers are fixed, readable strings rather than generated ULIDs so a
 * failing assertion names something recognisable.
 */
const AT = '2026-01-05T15:00:00.000Z';
const BY = 'uid-alex';

export const FIXTURE_COMPANY_ID = 'company-contour';
export const FIXTURE_JOB_ID = 'job-saguaro';
/**
 * A second job, which **nobody external is on** (SCRUM-294).
 *
 * The fixtures held exactly one job until 2026-10-30, and that is why
 * a real bug shipped: with one job, a job list filtered by assignment
 * and a job list not filtered at all look identical. A client given
 * access to one job saw every job in the company in the portal's
 * picker, and no test in either suite could tell.
 *
 * So this exists to be *absent* — from the client's picker, from the
 * sub's, and from the sidebar of an internal member who is assigned
 * rather than company-wide. A test that counts jobs and finds two has
 * found the bug.
 */
export const FIXTURE_OTHER_JOB_ID = 'job-tempe';
/**
 * A third job, on which there is **no external member at all**.
 *
 * `FIXTURE_OTHER_JOB_ID` stopped being able to play that part when the
 * client was given two projects, which SCRUM-297 needs: a client with
 * one project cannot demonstrate a tab losing their choice. So the
 * absence case moved here, and this job exists to appear in nobody's
 * portal.
 */
export const FIXTURE_UNSHARED_JOB_ID = 'job-yuma';
export const FIXTURE_OWNER_UID = 'uid-alex';
export const FIXTURE_FIELD_UID = 'uid-mike';
/**
 * A coordinator on a custom role, declared here with the other identities
 * because `fixtureMemberships` reads it — the temporal dead zone recorded in
 * `docs/WORKING_NOTES.md` bit again, and the typechecker caught it this time
 * because the value is used in a module-level array.
 */
/**
 * The homeowner, and the contact their membership came from.
 *
 * Both halves again, for the same reason as the sub: the contact id is what
 * the job's `ownerContactIds` points at, and the uid is what the portal
 * signs in as. Without a client membership the owner portal had nobody to
 * be, so nothing could reach it — the same gap as a use case with no screen.
 */
export const FIXTURE_CLIENT_UID = 'uid-steven';
export const FIXTURE_CLIENT_CONTACT_ID = 'contact-steven';

export const FIXTURE_COORDINATOR_UID = 'uid-robin';
/**
 * A project manager, added for P5-05.
 *
 * The matrix has had this role since Phase 1 and the demo had nobody in
 * it, which left one distinction untestable from the UI: a project manager
 * has `estimates: edit` and **not** the `approveEstimates` flag, so they
 * price a job and somebody else commits to it. With only an administrator
 * (who has both) and a coordinator (who has neither) there was no way to
 * see the Approve button correctly absent.
 */
export const FIXTURE_PM_UID = 'uid-priya';
export const FIXTURE_COORDINATOR_ROLE_ID = 'role-coordinator';

/**
 * An **expediter**: files purchase orders, chases deliveries, and is not
 * shown what any trade charged.
 *
 * Exists for one reason — to make a gate observable. No built-in role
 * holds `purchaseOrders` without also carrying `seeBuilderCost`, and the
 * coordinator who lacks the flag holds `purchaseOrders: none`, so the
 * *Related bids* section on a purchase order gated its amounts on a flag
 * no test could watch. An unobserved gate is the thing this fixture has
 * already been bitten by twice: the coordinator is here because a
 * mutation widening the bids write gate from `edit` to `view` survived
 * unnoticed, and the same happened to the time clock's.
 *
 * Kept separate from the coordinator rather than widening them, because
 * `purchase-orders.e2e.ts` asserts the coordinator is refused purchase
 * orders outright — a persona whose absences are tested is not a
 * persona to add presences to.
 */
export const FIXTURE_EXPEDITER_UID = 'uid-sam';
export const FIXTURE_EXPEDITER_ROLE_ID = 'role-expediter';
/**
 * A subcontractor, with the contact record their membership came from.
 *
 * Both halves are needed to exercise anything a sub does: the membership
 * says which jobs they are on, and the contact id is what their schedule
 * confirmations are keyed by. Without a fixture like this the sub portal
 * could only be tested by hand.
 */
export const FIXTURE_SUB_UID = 'uid-dana';
export const FIXTURE_SUB_CONTACT_ID = 'contact-dana-electric';

export const fixtureCompany: Company = {
  id: FIXTURE_COMPANY_ID,
  name: 'Sonoran Fuel Systems',
  timeZone: 'America/Phoenix',
  currency: 'USD',
  accountingMethod: 'accrual',
  workWeek: DEFAULT_WORK_WEEK,
  phone: '+1-402-555-0142',
  email: 'office@sonoranfuel.test',
  address: {
    line1: '4500 E Van Buren St',
    city: 'Phoenix',
    state: 'AZ',
    postalCode: '85008',
    country: 'US',
  },
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
};

export const fixtureJob: Job = {
  id: FIXTURE_JOB_ID,
  companyId: FIXTURE_COMPANY_ID,
  name: 'Saguaro Fuel Mart #12 - Tank Replacement',
  prefix: 'SFM',
  status: 'open',
  timeZone: 'America/Phoenix',
  address: {
    line1: '4500 E Van Buren St',
    city: 'Phoenix',
    state: 'AZ',
    postalCode: '85008',
    country: 'US',
    latitude: 33.4515,
    longitude: -112.0192,
  },
  ownerContactIds: ['contact-steven'],
  projectManagerUids: [FIXTURE_OWNER_UID],
  internalUids: [FIXTURE_OWNER_UID, FIXTURE_FIELD_UID],
  subContactIds: [FIXTURE_SUB_CONTACT_ID],
  startDate: '2026-09-11',
  contractType: 'fixed',
  tags: [],
  scheduleOnline: false,
  // Budget on, which is **not** the default. `DEFAULT_PORTAL_MODULES` has
  // it off because most fixed-price residential jobs should not expose a
  // cost-code breakdown. This one is a commercial fuel station whose owner
  // is a company with an accountant, which is the case the owner's budget
  // page exists for — and seeding it off would leave P5-08 unreachable
  // from the demo.
  portalModules: { ...DEFAULT_PORTAL_MODULES, budget: true },
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
};

/** See `FIXTURE_OTHER_JOB_ID`: present so that absence can be tested. */
export const fixtureOtherJob: Job = {
  id: FIXTURE_OTHER_JOB_ID,
  companyId: FIXTURE_COMPANY_ID,
  // Named to sort **after** Saguaro, so that adding it moves no
  // default: every screen that lands on the first job lands where it
  // always did, and the only thing this job changes is what a list of
  // jobs *contains*.
  name: 'Tempe Marketplace - Forecourt Rebuild',
  prefix: 'TMF',
  status: 'open',
  timeZone: 'America/Phoenix',
  address: {
    line1: '1200 W Rio Salado Pkwy',
    city: 'Tempe',
    state: 'AZ',
    postalCode: '85281',
    country: 'US',
  },
  // Nobody external, and no owner contact: this job is not theirs.
  ownerContactIds: [],
  projectManagerUids: [FIXTURE_OWNER_UID],
  internalUids: [FIXTURE_OWNER_UID],
  subContactIds: [],
  startDate: '2026-09-18',
  contractType: 'fixed',
  tags: [],
  scheduleOnline: false,
  // The product defaults, deliberately: this job is nobody's but the
  // builder's, so nothing about it should be unusual.
  portalModules: { ...DEFAULT_PORTAL_MODULES },
  createdAt: AT,
  createdBy: FIXTURE_OWNER_UID,
  updatedAt: AT,
  updatedBy: FIXTURE_OWNER_UID,
};

/** See `FIXTURE_UNSHARED_JOB_ID`: present so that absence can be tested. */
export const fixtureUnsharedJob: Job = {
  id: FIXTURE_UNSHARED_JOB_ID,
  companyId: FIXTURE_COMPANY_ID,
  // Sorts last, so adding it moves no screen's default.
  name: 'Yuma Crossing - Canopy Replacement',
  prefix: 'YCC',
  status: 'open',
  timeZone: 'America/Phoenix',
  address: {
    line1: '300 S 4th Ave',
    city: 'Yuma',
    state: 'AZ',
    postalCode: '85364',
    country: 'US',
  },
  ownerContactIds: [],
  projectManagerUids: [FIXTURE_OWNER_UID],
  internalUids: [FIXTURE_OWNER_UID],
  subContactIds: [],
  startDate: '2026-09-25',
  contractType: 'fixed',
  tags: [],
  scheduleOnline: false,
  portalModules: { ...DEFAULT_PORTAL_MODULES },
  createdAt: AT,
  createdBy: FIXTURE_OWNER_UID,
  updatedAt: AT,
  updatedBy: FIXTURE_OWNER_UID,
};

export const fixtureMemberships: Membership[] = [
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_OWNER_UID,
    kind: 'internal',
    roleId: 'role-admin',
    jobAccess: 'all',
    jobIds: [],
    status: 'active',
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_FIELD_UID,
    kind: 'internal',
    roleId: 'role-field',
    jobAccess: 'assigned',
    jobIds: [FIXTURE_JOB_ID],
    status: 'active',
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_PM_UID,
    kind: 'internal',
    roleId: 'role-project-manager',
    jobAccess: 'all',
    jobIds: [],
    status: 'active',
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_COORDINATOR_UID,
    kind: 'internal',
    roleId: FIXTURE_COORDINATOR_ROLE_ID,
    jobAccess: 'all',
    jobIds: [],
    status: 'active',
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_EXPEDITER_UID,
    kind: 'internal',
    roleId: FIXTURE_EXPEDITER_ROLE_ID,
    jobAccess: 'all',
    jobIds: [],
    status: 'active',
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_CLIENT_UID,
    kind: 'client',
    roleId: 'role-client',
    // Never 'all', for the same reason as a sub: an owner sees their own
    // project and nothing else.
    jobAccess: 'assigned',
    // **Two**, which SCRUM-297 needs: a client with one project cannot
    // show a tab losing the project they chose.
    jobIds: [FIXTURE_JOB_ID, FIXTURE_OTHER_JOB_ID],
    status: 'active',
    contactId: FIXTURE_CLIENT_CONTACT_ID,
  },
  {
    companyId: FIXTURE_COMPANY_ID,
    companyName: fixtureCompany.name,
    uid: FIXTURE_SUB_UID,
    kind: 'sub',
    roleId: 'role-sub',
    // Never 'all': an external member sees only the jobs they are on, and a
    // fixture that got this wrong would hide the bug it exists to catch.
    jobAccess: 'assigned',
    jobIds: [FIXTURE_JOB_ID],
    status: 'active',
    contactId: FIXTURE_SUB_CONTACT_ID,
  },
];

/**
 * Role documents for every built-in template, derived from the same
 * BUILT_IN_ROLES the app and the rules tests use so they cannot drift.
 *
 * Without these the in-memory app resolves no permissions at all: the session
 * builds a Principal from a role document, so every gated control stayed
 * hidden on the zero-setup path and the whole app looked read-only.
 */
/**
 * A custom role, because every built-in one that can see change orders also
 * carries `seeBuilderCost`.
 *
 * Without this there is no way to exercise the gate the feature doc asks for
 * — "Builder Cost column hidden without `seeBuilderCost`" — and the only
 * screen that honours it would be untested from the outside. A coordinator
 * who schedules work and quotes owner prices, and has no business knowing
 * the company's margin, is also a real job.
 */
const coordinatorRole: Role = {
  id: FIXTURE_COORDINATOR_ROLE_ID,
  companyId: FIXTURE_COMPANY_ID,
  name: 'Project coordinator',
  builtIn: false,
  modules: {
    ...BUILT_IN_ROLES['role-project-manager'].modules,
    /**
     * Reads the company's time and clocks none of it.
     *
     * A coordinator is not on the tools, so `edit` would be a button they
     * never press. It is also the only persona in this fixture that
     * separates reading time from recording it — which the time clock's
     * permission gate needs, and did not have: every test of that gate
     * used somebody holding the module at `edit` or not at all, so a
     * mutation widening it from `edit` to `view` survived unnoticed.
     */
    timeClock: 'view',
    /**
     * Reads a tender and runs none of it — and sees no prices in it.
     *
     * A coordinator quotes owner prices and has no business knowing the
     * company's margin, and a bid is exactly that: what a trade charges
     * the builder, trade by trade. This is also the only persona in the
     * fixture holding a module at `view` while others hold `edit`, which
     * the bids page's write gate needed and did not have — a mutation
     * widening it from `edit` to `view` survived unnoticed.
     */
    bids: 'view',
    // Everything a project manager has, minus the money they do not need.
    budget: 'none',
    estimates: 'none',
    purchaseOrders: 'none',
    bills: 'none',
    invoices: 'none',
    payments: 'none',
  },
  // The point of the fixture: no `seeBuilderCost`.
  flags: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
};

/**
 * An expediter: reads purchase orders, reads a tender, sees no amount
 * anybody quoted.
 *
 * **The only persona holding `purchaseOrders` without
 * `seeBuilderCost`**, which is the whole reason it exists — see
 * `FIXTURE_EXPEDITER_ROLE_ID`.
 *
 * The split it models is a real one rather than a contrivance. A
 * purchase order is the company's own commitment to one trade, and
 * somebody administering it has to see what it is worth; the *Related
 * bids* on it are other trades' prices, which is competitive
 * information of a different kind. The gate is the same `seeBuilderCost`
 * the bids screen uses, because the same number must not be readable on
 * one screen and hidden on another.
 */
const expediterRole: Role = {
  id: FIXTURE_EXPEDITER_ROLE_ID,
  companyId: FIXTURE_COMPANY_ID,
  name: 'Expediter',
  builtIn: false,
  modules: {
    ...Object.fromEntries(MODULES.map((module) => [module, 'none'])),
    jobs: 'view',
    schedule: 'view',
    purchaseOrders: 'view',
    bids: 'view',
    /**
     * Needed, not generous. Every screen that names a trade reads it
     * from the directory, so a role without this sees "A trade"
     * wherever a name belongs — which is a person who cannot do the job
     * rather than a person kept from something. The same fallback once
     * reached the bids page for read-only viewers, because its contacts
     * read was gated on `edit`.
     */
    directory: 'view',
    /**
     * Reads the portfolio and none of its money.
     *
     * The combination is the point, and it is a realistic one: an
     * expediter chases deliveries, so *which jobs are slipping* is
     * their daily question, while the budget, the receivables and the
     * payables are not theirs. It is also the only persona that can
     * watch the reports page decline to offer a report it cannot
     * produce — and that gate is not politeness, it is what stops a
     * refused query killing the page.
     */
    reports: 'view',
  } as Role['modules'],
  // The point of the fixture: no `seeBuilderCost`.
  flags: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
};

export const fixtureRoles: Role[] = [
  coordinatorRole,
  expediterRole,
  ...Object.entries(BUILT_IN_ROLES).map(([roleId, role]) => ({
    id: roleId,
    companyId: FIXTURE_COMPANY_ID,
    name: role.name,
    builtIn: true,
    // Copied rather than referenced: BUILT_IN_ROLES is deeply readonly, and a
    // fixture is a document someone may edit in the running app.
    modules: { ...role.modules },
    flags: [...role.flags],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  })),
];

export const fixtureUsers = [
  {
    uid: FIXTURE_OWNER_UID,
    email: 'alex@sonoranfuel.test',
    displayName: 'Alex McKay',
    password: 'password123',
  },
  {
    uid: FIXTURE_FIELD_UID,
    email: 'mike@sonoranfuel.test',
    displayName: 'Mike Stibbs',
    password: 'password123',
  },
  {
    uid: FIXTURE_PM_UID,
    email: 'priya@sonoranfuel.test',
    displayName: 'Priya Raman',
    password: 'password123',
  },
  {
    uid: FIXTURE_COORDINATOR_UID,
    email: 'robin@sonoranfuel.test',
    displayName: 'Robin Lee',
    password: 'password123',
  },
  {
    uid: FIXTURE_EXPEDITER_UID,
    email: 'sam@sonoranfuel.test',
    displayName: 'Sam Ortiz',
    password: 'password123',
  },
  {
    uid: FIXTURE_CLIENT_UID,
    email: 'steven@example.test',
    displayName: 'Steven Vance',
    password: 'password123',
  },
  {
    uid: FIXTURE_SUB_UID,
    email: 'dana@danaelectric.test',
    displayName: 'Dana Ruiz',
    password: 'password123',
  },
] as const;

/**
 * A fixture login by uid.
 *
 * Exists because three end-to-end tests destructured `fixtureUsers` by
 * position — `const [admin, , sub] = fixtureUsers` — and adding a fourth
 * user in the middle silently handed them the wrong person. Five tests
 * failed with "expected /sub, got /signin", which says nothing about the
 * cause. Ask for who you mean.
 */
export function fixtureUser(uid: string): (typeof fixtureUsers)[number] {
  const found = fixtureUsers.find((user) => user.uid === uid);
  if (!found) throw new Error(`No fixture user ${uid}`);
  return found;
}

/** Membership documents, the per-company half of what a Principal needs. */
export const fixtureMembers: Member[] = fixtureMemberships.map((membership) => ({
  id: membership.uid,
  // Name and address carried onto the member record, not only onto the
  // login. The members list reads them, and email notifications are sent to
  // the address on the membership — a member without one gets the in-app
  // notification and no mail, which is correct behaviour and a useless
  // fixture.
  ...(() => {
    const user = fixtureUsers.find((candidate) => candidate.uid === membership.uid);
    return user ? { displayName: user.displayName, email: user.email } : {};
  })(),
  companyId: membership.companyId,
  kind: membership.kind,
  roleId: membership.roleId,
  status: membership.status,
  jobAccess: membership.jobAccess,
  jobIds: [...(membership.jobIds ?? [])],
  // Spread rather than assigned: internal members have no contact, and
  // Firestore refuses an explicit undefined rather than dropping the key.
  ...(membership.contactId === undefined ? {} : { contactId: membership.contactId }),
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
}));

/**
 * The contact records the external memberships came from.
 *
 * A sub membership is only half of a subcontractor: the contact is what a
 * schedule item is assigned to and what a confirmation is keyed by, so
 * without this the assignee picker has nothing to offer and the sub portal
 * is unreachable.
 */
export const fixtureContacts: Contact[] = [
  {
    /**
     * A supplier, added 2026-10-29 for SCRUM-284.
     *
     * The directory held a subcontractor and a customer and **no
     * vendor at all**, which made several things untestable and one of
     * them unusable: the supplier picker on a catalog item had nothing
     * to offer, and "who do we buy this from" cannot be answered by a
     * company with nobody to buy from. A fuel-systems builder has
     * merchants; the fixtures now say so.
     */
    id: 'contact-jf-petroleum',
    companyId: FIXTURE_COMPANY_ID,
    kind: 'vendor',
    name: 'JF Petroleum Group',
    companyName: 'JF Petroleum Group',
    email: 'orders@jfpetro.test',
    trade: 'Fuel systems supply',
    status: 'active',
    certificates: [],
    defaultCostCodeIds: [],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    /**
     * A merchant with somebody to ask for (SCRUM-303).
     *
     * JF Petroleum's `name` and `companyName` are the same string, so
     * it cannot show what a supplier row is *for*: the company in bold
     * and the representative under it. This one has both, and exists
     * so that difference is visible to a test rather than only to a
     * person looking at dev.
     */
    id: 'contact-sun-devil-pipe',
    companyId: FIXTURE_COMPANY_ID,
    kind: 'vendor',
    name: 'Marcela Ortiz',
    companyName: 'Sun Devil Pipe & Supply',
    email: 'marcela@sundevilpipe.test',
    trade: 'Pipe and fittings',
    status: 'active',
    certificates: [],
    defaultCostCodeIds: [],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: FIXTURE_SUB_CONTACT_ID,
    companyId: FIXTURE_COMPANY_ID,
    kind: 'sub',
    name: 'Dana Ruiz',
    companyName: 'Dana Electric',
    email: 'dana@danaelectric.test',
    trade: 'Electrical',
    status: 'active',
    certificates: [],
    defaultCostCodeIds: [],
    // Links the contact to the login, which is how a sub's own confirmation
    // can be traced back to the person who answered it.
    memberUid: FIXTURE_SUB_UID,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: FIXTURE_CLIENT_CONTACT_ID,
    companyId: FIXTURE_COMPANY_ID,
    kind: 'customer',
    name: 'Steven Vance',
    email: 'steven@example.test',
    status: 'active',
    // Links the contact to the login, which is how an owner-visible log or
    // change order finds somebody to notify.
    memberUid: FIXTURE_CLIENT_UID,
    certificates: [],
    defaultCostCodeIds: [],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

/**
 * Folders and files on the fixture job.
 *
 * Enough to exercise every state the views have to render: a nested folder,
 * a system folder nobody may rename, a file in a folder and one loose at the
 * root, one shared with the owner, one still being checked and one blocked.
 * Without the last two those badges would only ever be seen by their unit
 * tests.
 */
export const fixtureFolders: Folder[] = [
  {
    id: 'folder-permits',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    name: 'Permits',
    kind: 'documents',
    visibility: { internal: true, subs: false, owner: false, private: false },
    system: false,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: 'folder-permits-electrical',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    name: 'Electrical',
    parentId: 'folder-permits',
    kind: 'documents',
    visibility: { internal: true, subs: false, owner: false, private: false },
    system: false,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: 'folder-site-photos',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    name: 'Site photos',
    kind: 'photos',
    visibility: { internal: true, subs: true, owner: true, private: false },
    system: false,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: 'folder-from-subs',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    name: 'From Subs',
    kind: 'documents',
    visibility: { internal: true, subs: true, owner: false, private: false },
    system: true,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

const fixtureFile = (
  id: string,
  name: string,
  contentType: string,
  size: number,
  over: Partial<File> = {},
): File => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  name,
  storagePath: `${FIXTURE_COMPANY_ID}/${FIXTURE_JOB_ID}/${folderKindFor(contentType)}/${id}`,
  size,
  contentType,
  jobId: FIXTURE_JOB_ID,
  uploadedBy: BY,
  status: 'ready',
  visibility: { internal: true, subs: false, owner: false, private: false },
  tagIds: [],
  linkedRecords: [],
  linkedKeys: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
  ...over,
});

export const fixtureFiles: File[] = [
  // Internal only, and attached to the same log as the owner-visible
  // photograph below. That pairing is the point: the portal's attachment
  // strip must show one of these two and not the other, so a filter that
  // returned everything is visible in the e2e rather than only in a unit
  // test.
  fixtureFile('file-permit', 'Permit application.pdf', 'application/pdf', 182_000, {
    folderId: 'folder-permits',
    createdAt: '2026-01-02T15:00:00.000Z',
    linkedRecords: [{ kind: 'dailyLog', id: 'log-framing', jobId: FIXTURE_JOB_ID }],
    linkedKeys: ['dailyLog:log-framing'],
  }),
  fixtureFile('file-plan', 'Site plan.pdf', 'application/pdf', 4_400_000, {
    visibility: { internal: true, subs: false, owner: true, private: false },
    createdAt: '2026-01-03T15:00:00.000Z',
  }),
  fixtureFile('file-framing', 'Old tank removal.jpg', 'image/jpeg', 2_100_000, {
    folderId: 'folder-site-photos',
    visibility: { internal: true, subs: true, owner: true, private: false },
    createdAt: '2026-01-04T15:00:00.000Z',
    // Attached to the framing log, so the attachment strip has something to
    // show without anybody having to attach it first.
    linkedRecords: [{ kind: 'dailyLog', id: 'log-framing', jobId: FIXTURE_JOB_ID }],
    linkedKeys: ['dailyLog:log-framing'],
  }),
  // Uploaded but not yet checked. The in-memory adapter has no processing
  // pipeline — that is a Firestore trigger — so this is also what an upload
  // looks like in `pnpm dev` until P3-05f.
  fixtureFile('file-rebar', 'Rebar before pour.jpg', 'image/jpeg', 3_300_000, {
    status: 'processing',
    createdAt: '2026-01-05T09:00:00.000Z',
  }),
  // Failed its checks. Must still be listed, or nobody could delete it.
  fixtureFile('file-walkthrough', 'Walkthrough.mp4', 'video/mp4', 620_000_000, {
    status: 'blocked',
    blockedReason: 'tooLarge',
    createdAt: '2026-01-05T10:00:00.000Z',
  }),
  // Somebody else's private file, so the two-query read path is exercised by
  // every run rather than only by its unit tests: this must not appear for
  // Alex, and must appear for Mike.
  fixtureFile('file-private-note', 'Site visit notes.pdf', 'application/pdf', 44_000, {
    uploadedBy: FIXTURE_FIELD_UID,
    createdBy: FIXTURE_FIELD_UID,
    updatedBy: FIXTURE_FIELD_UID,
    visibility: { internal: false, subs: false, owner: false, private: true },
    createdAt: '2026-01-05T11:00:00.000Z',
  }),
];

/**
 * A few days of logs on the fixture job.
 *
 * One owner-visible with a photograph attached, one internal-only, and one
 * private to the field user — so the two-query read path, the audience badge
 * and the attachment strip are all exercised without anybody setting them up
 * by hand.
 */
const fixtureDailyLog = (
  id: string,
  date: string,
  title: string,
  over: Partial<DailyLog> = {},
): DailyLog => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  jobId: FIXTURE_JOB_ID,
  date,
  title,
  notes: '',
  tagIds: [],
  customFields: {},
  visibility: { internal: true, subs: false, owner: false, private: false },
  notifyUids: [],
  createdAt: `${date}T18:00:00.000Z`,
  createdBy: BY,
  updatedAt: `${date}T18:00:00.000Z`,
  updatedBy: BY,
  ...over,
});

export const fixtureDailyLogs: DailyLog[] = [
  fixtureDailyLog('log-framing', '2026-01-04', 'Old tanks removed', {
    notes: 'Both 12,000 gallon tanks lifted out and hauled off. Pit open and shored.',
    visibility: { internal: true, subs: true, owner: true, private: false },
    customFields: { onSite: [FIXTURE_FIELD_UID] },
    // Recorded weather on one log, so the read-only display is exercised by
    // every run. Nothing fetches it yet — see P3-07 and `WeatherPort`.
    weather: {
      summary: 'Clear and cold',
      icon: 'clear',
      highF: 34,
      lowF: 18,
      windMph: 7,
      humidityPct: 54,
      precipHundredthsIn: 0,
      fetchedAt: '2026-01-04T18:00:00.000Z',
    },
    weatherNotes: 'Cold and dry all day',
    tagIds: ['tag-weather'],
  }),
  fixtureDailyLog('log-slab', '2026-01-02', 'Slab pour', {
    notes: 'Pumped from the street. Finishers left at four.',
  }),
  fixtureDailyLog('log-private', '2026-01-05', 'Site visit notes', {
    notes: 'Spoke to the inspector about the setback.',
    visibility: { internal: false, subs: false, owner: false, private: true },
    createdBy: FIXTURE_FIELD_UID,
    updatedBy: FIXTURE_FIELD_UID,
  }),
];

/**
 * A few to-dos on the fixture job.
 *
 * One with a typed date already past, one following a schedule item, one
 * done, and one private to the field user — so overdue styling, the
 * schedule-linked due date, the completed state and the two-query read path
 * are all exercised by every run.
 */
const fixtureTodo = (id: string, title: string, over: Partial<Todo> = {}): Todo => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  jobId: FIXTURE_JOB_ID,
  title,
  notes: '',
  assignees: [],
  assigneeIds: [],
  checklist: [],
  priority: 'medium',
  tagIds: [],
  reminder: 'none',
  visibility: { internal: true, subs: false, owner: false, private: false },
  completed: false,
  linkedRecords: [],
  linkedKeys: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
  ...over,
});

export const fixtureTodos: Todo[] = [
  fixtureTodo('todo-windows', 'Order dispensers', {
    priority: 'highest',
    due: { kind: 'date', date: '2026-01-20' },
    assignees: [{ kind: 'user', id: FIXTURE_FIELD_UID, notify: true }],
    assigneeIds: [FIXTURE_FIELD_UID],
    tagIds: ['tag-warranty'],
    notes: 'Lead time is six weeks, so this is the long pole.',
  }),
  fixtureTodo('todo-crane', 'Book the vac truck', {
    priority: 'high',
    // A plain date, deliberately: the schedule starts empty in the fixtures
    // (a schedule end-to-end test asserts that), so a seeded link would
    // point at nothing and show as broken. The schedule-linked case is
    // covered end to end in `todos.e2e.ts`, which builds the item first.
    due: { kind: 'date', date: '2026-02-02' },
  }),
  fixtureTodo('todo-permit', 'File the permit', {
    completed: true,
    completedAt: AT,
    completedBy: BY,
    due: { kind: 'date', date: '2026-01-08' },
  }),
  fixtureTodo('todo-private', 'Ring the lawyer', {
    visibility: { internal: false, subs: false, owner: false, private: true },
    createdBy: FIXTURE_FIELD_UID,
    updatedBy: FIXTURE_FIELD_UID,
  }),
];

/**
 * A company's tags.
 *
 * Two, because one tag proves nothing about filtering and the pickers need
 * something to show. Shared across modules, which is the point of them
 * being company-level.
 */
/**
 * The job's schedule: replacing the underground tanks at a fuel station.
 *
 * Twenty-four activities with real predecessors, from the critical path the
 * maintainer supplied. It is here rather than invented because a demo
 * schedule that is a list of pretty bars teaches nobody anything: this one
 * has the two features that make scheduling software worth having.
 *
 * **The regulatory inspection is an activity, not a note.** Arizona's ADEQ
 * has to see the installation before it is covered, so the inspection is a
 * predecessor of the backfill. The schedule therefore *cannot* show backfill
 * starting before the inspection is done, and moving the inspection moves
 * the opening date — which is the whole point of a dependency rather than a
 * reminder in a comment.
 *
 * **Not everything is critical.** Ordering tanks (45 days) runs alongside
 * permitting (30 days) from the same predecessor, so one of them has float
 * and the other does not. Shortening the wrong one buys nothing, and that
 * is the thing a Gantt chart is for.
 *
 * The contamination branch the maintainer described — soil sampling finds
 * something, everything stops for remediation — is modelled as the change
 * order `co-0001`, because that is what it is: unplanned work, priced, that
 * the owner has to approve.
 *
 * Dates are **derived** over the job calendar rather than typed in, so the
 * fixture cannot drift from the arithmetic the product itself does.
 */
interface Activity {
  readonly id: string;
  readonly title: string;
  readonly workDays: number;
  /** Ids of the activities that must finish first. */
  readonly after: readonly string[];
  readonly phaseId: string;
  readonly colour: ScheduleColour;
  readonly notes?: string;
  /** Left off the owner's view: procurement is the builder's business. */
  readonly internalOnly?: boolean;
  /** Contact ids doing the work, for the sub portal. */
  readonly subs?: readonly string[];
}

const ACTIVITIES: readonly Activity[] = [
  {
    id: 'eng',
    title: 'Engineering / tank design',
    workDays: 10,
    after: [],
    phaseId: 'phase-design',
    colour: 'slate',
  },
  {
    id: 'permit-submit',
    title: 'Submit permits',
    workDays: 1,
    after: ['eng'],
    phaseId: 'phase-design',
    colour: 'slate',
  },
  {
    id: 'permit-approve',
    title: 'Permit / regulatory approval',
    workDays: 30,
    after: ['permit-submit'],
    phaseId: 'phase-design',
    colour: 'amber',
    notes: 'Twenty to forty-five days in practice. Nothing on site can start without it.',
  },
  {
    id: 'order-tanks',
    title: 'Order tanks and equipment',
    workDays: 45,
    after: ['eng'],
    phaseId: 'phase-design',
    colour: 'amber',
    internalOnly: true,
    notes: 'Thirty to sixty days. Runs alongside permitting, and is usually the longer of the two.',
  },
  {
    id: 'mobilise',
    title: 'Mobilise and close forecourt',
    workDays: 1,
    after: ['permit-approve', 'order-tanks'],
    phaseId: 'phase-removal',
    colour: 'red',
    notes: 'The station stops selling fuel here. Every day after this one costs the owner trade.',
  },
  {
    id: 'pump-out',
    title: 'Pump out, clean and inert tanks',
    workDays: 1,
    after: ['mobilise'],
    phaseId: 'phase-removal',
    colour: 'red',
  },
  {
    id: 'excavate',
    title: 'Excavation and old tank removal',
    workDays: 3,
    after: ['pump-out'],
    phaseId: 'phase-removal',
    colour: 'red',
  },
  {
    id: 'soil-sample',
    title: 'Closure / soil inspection and sampling',
    workDays: 2,
    after: ['excavate'],
    phaseId: 'phase-removal',
    colour: 'red',
    notes:
      'If the samples come back contaminated, everything below stops until remediation is cleared. See change order 0001.',
  },
  {
    id: 'bedding',
    title: 'Excavation preparation and bedding',
    workDays: 1,
    after: ['soil-sample'],
    phaseId: 'phase-install',
    colour: 'blue',
  },
  {
    id: 'set-tanks',
    title: 'Set new tanks',
    workDays: 1,
    after: ['bedding'],
    phaseId: 'phase-install',
    colour: 'blue',
  },
  {
    id: 'anchor',
    title: 'Anchor and backfill around tanks',
    workDays: 2,
    after: ['set-tanks'],
    phaseId: 'phase-install',
    colour: 'blue',
  },
  {
    id: 'piping',
    title: 'Product, vent and vapour piping',
    workDays: 4,
    after: ['anchor'],
    phaseId: 'phase-install',
    colour: 'blue',
  },
  {
    id: 'sumps',
    title: 'Sumps, STPs and containment',
    workDays: 3,
    after: ['piping'],
    phaseId: 'phase-install',
    colour: 'blue',
  },
  {
    id: 'electrical',
    title: 'Electrical, ATG and leak detection',
    workDays: 3,
    after: ['sumps'],
    phaseId: 'phase-install',
    colour: 'violet',
    subs: [FIXTURE_SUB_CONTACT_ID],
  },
  {
    id: 'pressure-test',
    title: 'Pressure and integrity testing',
    workDays: 1,
    after: ['piping', 'sumps', 'electrical'],
    phaseId: 'phase-inspect',
    colour: 'teal',
  },
  {
    id: 'adeq',
    title: 'ADEQ regulatory inspection',
    workDays: 1,
    after: ['pressure-test'],
    phaseId: 'phase-inspect',
    colour: 'teal',
    notes:
      'An activity rather than a note, deliberately: it has to happen before anything is covered, so the schedule cannot show backfill starting first.',
  },
  {
    id: 'backfill',
    title: 'Final backfill and compaction',
    workDays: 2,
    after: ['adeq'],
    phaseId: 'phase-surface',
    colour: 'green',
  },
  {
    id: 'concrete',
    title: 'Concrete and islands',
    workDays: 4,
    after: ['backfill'],
    phaseId: 'phase-surface',
    colour: 'green',
  },
  {
    id: 'cure',
    title: 'Concrete cure',
    workDays: 5,
    after: ['concrete'],
    phaseId: 'phase-surface',
    colour: 'green',
    notes: 'Three to seven days. Nobody can stand on it and no dispenser can be set.',
  },
  {
    id: 'dispensers',
    title: 'Dispensers and fuel equipment',
    workDays: 3,
    after: ['cure'],
    phaseId: 'phase-surface',
    colour: 'green',
  },
  {
    id: 'commissioning',
    title: 'POS, fuel controls and electrical commissioning',
    workDays: 2,
    after: ['dispensers'],
    phaseId: 'phase-open',
    colour: 'violet',
    subs: [FIXTURE_SUB_CONTACT_ID],
  },
  {
    id: 'fuel-delivery',
    title: 'Fuel delivery',
    workDays: 1,
    after: ['commissioning'],
    phaseId: 'phase-open',
    colour: 'blue',
  },
  {
    id: 'startup',
    title: 'Startup, testing and final approval',
    workDays: 2,
    after: ['fuel-delivery'],
    phaseId: 'phase-open',
    colour: 'teal',
  },
  {
    id: 'open',
    title: 'Open station',
    workDays: 1,
    after: ['startup'],
    phaseId: 'phase-open',
    colour: 'green',
    notes: 'The milestone the whole schedule is for.',
  },
];

export const fixtureSchedulePhases: SchedulePhase[] = [
  ['phase-design', 'Design and permitting', 'slate'],
  ['phase-removal', 'Shutdown and removal', 'red'],
  ['phase-install', 'New tank installation', 'blue'],
  ['phase-inspect', 'Testing and inspection', 'teal'],
  ['phase-surface', 'Surface and equipment', 'green'],
  ['phase-open', 'Commissioning and opening', 'violet'],
].map(([id, name, colour], index) => ({
  id: id as string,
  companyId: FIXTURE_COMPANY_ID,
  jobId: FIXTURE_JOB_ID,
  name: name as string,
  colour: colour as ScheduleColour,
  order: index,
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
}));

/** Monday, and the day engineering starts. */
const SCHEDULE_START = '2026-09-07';

/**
 * Walk the activities in order, giving each the first working day after
 * everything it waits for.
 *
 * Computed with the same `addWorkingDays` the product uses rather than
 * typed out, because a fixture with hand-written dates is a fixture that
 * disagrees with the calendar the moment either changes.
 */
function scheduleItems(): ScheduleItem[] {
  const calendar = jobCalendar(DEFAULT_WORK_WEEK, [], FIXTURE_JOB_ID);
  const ends = new Map<string, DateOnly>();
  const items: ScheduleItem[] = [];

  for (const activity of ACTIVITIES) {
    const start =
      activity.after.length === 0
        ? nextWorkingDay(calendar, SCHEDULE_START as DateOnly)
        : activity.after
            .map((id) => addWorkingDays(calendar, ends.get(id)!, 1))
            .reduce((latest, candidate) => (candidate > latest ? candidate : latest));
    // A one-day activity ends the day it starts, which is why the offset is
    // one less than the duration.
    const end = addWorkingDays(calendar, start, activity.workDays - 1);
    ends.set(activity.id, end);

    items.push({
      id: `item-${activity.id}`,
      companyId: FIXTURE_COMPANY_ID,
      jobId: FIXTURE_JOB_ID,
      title: activity.title,
      colour: activity.colour,
      phaseId: activity.phaseId,
      startDate: start,
      endDate: end,
      workDays: activity.workDays,
      progress: 0,
      completed: false,
      reminder: 'none',
      predecessors: activity.after.map((id) => ({
        itemId: `item-${id}`,
        type: 'FS' as const,
        lagDays: 0,
      })),
      notes: activity.notes ? { internal: activity.notes } : {},
      assignees: (activity.subs ?? []).map((id) => ({
        kind: 'contact' as const,
        id,
        notify: true,
      })),
      confirmations: {},
      shifts: [],
      tagIds: [],
      equipmentIds: [],
      materials: [],
      visibility: {
        internal: true,
        subs: !activity.internalOnly,
        owner: !activity.internalOnly,
        private: false,
      },
      createdAt: AT,
      createdBy: BY,
      updatedAt: AT,
      updatedBy: BY,
    } as ScheduleItem);
  }

  return items;
}

export const fixtureScheduleItems: ScheduleItem[] = scheduleItems();

export const fixtureTags: Tag[] = [
  {
    id: 'tag-weather',
    companyId: FIXTURE_COMPANY_ID,
    name: 'Weather delay',
    colour: 'amber',
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
  {
    id: 'tag-warranty',
    companyId: FIXTURE_COMPANY_ID,
    name: 'Warranty',
    colour: 'purple',
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

/**
 * One released change order with a priced line.
 *
 * Seeded rather than created by a test, because a Playwright test cannot
 * carry state across users: signing in navigates, which reloads the module
 * that builds the in-memory store, so anything created before a sign-out is
 * gone. A fixture survives, which is what lets the `seeBuilderCost` gate be
 * checked from two different logins.
 */
export const fixtureChangeOrders: ChangeOrder[] = [
  {
    id: 'co-island',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    number: '0001',
    title: 'Contaminated soil removal',
    description:
      'Contamination found under the north tank. Excavation, haul-off and ' +
      'confirmation sampling, as discussed on site.',
    status: 'released',
    priceMode: 'lineItems',
    lines: [
      {
        id: 'line-remediation',
        description: 'Contaminated soil removal and disposal',
        quantityThousandths: 1000,
        unitCost: { amount: 100_000, currency: 'USD' },
        markupMillionths: 200_000,
        showToOwner: true,
        // Coded, so approving this moves a budget row rather than landing
        // in the "no cost code" warning. The text was left over from a
        // kitchen remodel — "Island moved 600mm towards the window" — from
        // before the demo became a fuel station.
        costCodeId: 'code-remediation',
      },
    ],
    showLinesToOwner: true,
    // Far enough out that a fixture meant to be answered does not expire
    // before anybody looks at it. An expired one is a state worth seeding
    // too, but not as the only one.
    approvalDeadline: '2027-12-31',
    releasedAt: '2026-02-06T15:00:00.000Z',
    tagIds: [],
    linkedRecords: [],
    linkedKeys: [],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

/**
 * The owner's copy of the seeded change order.
 *
 * Seeded rather than derived, because the in-memory adapter has no
 * triggers: `onChangeOrderWritten` writes this in production and the dev
 * handlers write it when a change order is released, but a change order
 * that was released before the app started has nobody to have written it.
 * Built with the same `ownerChangeOrderFrom` the trigger uses, so it cannot
 * say anything the real one would not.
 */
export const fixtureOwnerChangeOrders = fixtureChangeOrders
  .filter((order) => isOwnerVisible(order.status))
  .map((order) => ownerChangeOrderFrom(order));

/**
 * One allowance and two selections against it.
 *
 * One released and unchosen, so the portal has a question waiting; one
 * released and already chosen **over** its allowance, because the overspend
 * is the figure the internal page exists to surface and a fixture that is
 * always under budget would never show it.
 */
export const fixtureAllowances: Allowance[] = [
  {
    id: 'allow-tiles',
    companyId: FIXTURE_COMPANY_ID,
    jobId: FIXTURE_JOB_ID,
    name: 'Dispenser allowance',
    amount: { amount: 200_000, currency: 'USD' },
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

const fixtureSelection = (id: string, title: string, over: Partial<Selection> = {}): Selection => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  jobId: FIXTURE_JOB_ID,
  title,
  description: '',
  choices: [],
  released: false,
  locked: false,
  visibility: { internal: true, subs: false, owner: true, private: false },
  tagIds: [],
  linkedRecords: [],
  linkedKeys: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
  ...over,
});

export const fixtureSelections: Selection[] = [
  // The ids are tiles and taps because this fixture was a kitchen before
  // the demo became a fuel station. Renaming them would touch every test
  // that names one; the **text** is what the maintainer reads on the
  // owner's portal, and that is what had to be about fuel (P5-07c).
  fixtureSelection('sel-tiles', 'Dispenser model', {
    description: 'Under the canopy, both islands.',
    location: 'Forecourt',
    category: 'Tanks and dispensers',
    allowanceId: 'allow-tiles',
    released: true,
    deadline: { kind: 'date', date: '2027-06-30' },
    choices: [
      {
        id: 'choice-porcelain',
        title: 'Six hose, card reader',
        description: 'Two per island, EMV card reader at the pump.',
        price: { amount: 240_000, currency: 'USD' },
      },
      {
        id: 'choice-ceramic',
        title: 'Four hose, card reader',
        price: { amount: 180_000, currency: 'USD' },
      },
    ],
  }),
  fixtureSelection('sel-taps', 'Canopy lighting', {
    location: 'Canopy',
    released: true,
    choices: [
      { id: 'choice-brass', title: 'LED canopy, 120 W' },
      {
        id: 'choice-chrome',
        title: 'LED canopy, 90 W',
        price: { amount: 42_000, currency: 'USD' },
      },
    ],
  }),
];

/**
 * A chart of accounts for the fuel-station demo (P5-01).
 *
 * Deliberately short and deliberately *about tank replacement*, not a
 * generic NAHB residential set — the demo job is a gas station having its
 * tanks replaced, and a chart full of "Drywall" and "Roofing" would make
 * every estimate line in the demo read as a mistake. The real NAHB-style
 * starter set offered on company creation is P5-02; this is what the seeded
 * company already uses.
 *
 * The ranges leave gaps between categories on purpose. A chart with
 * `1000-1999` then `2000-2999` has nowhere to put a new category between
 * them, and the first thing any real company does is add one.
 */
const costCodeCategory = (
  id: string,
  name: string,
  rangeStart: number,
  rangeEnd: number,
  order: number,
): CostCodeCategory => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  name,
  rangeStart,
  rangeEnd,
  order,
  active: true,
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
});

/**
 * One tax rate, so the invoice screen's picker is not empty (P5-12b).
 *
 * Arizona's transaction privilege tax, which is what a fuel station in
 * Phoenix would actually charge. A picker with nothing in it teaches
 * somebody that tax is unimplemented rather than unconfigured.
 */
export const fixtureTaxRates: TaxRate[] = [
  {
    id: 'tax-az-tpt',
    companyId: FIXTURE_COMPANY_ID,
    name: 'Arizona TPT',
    // 8.6% in ten-thousandths of a percent.
    rateMillionths: 86_000,
    active: true,
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

export const fixtureCostCodeCategories: CostCodeCategory[] = [
  costCodeCategory('cat-preliminaries', 'Preliminaries and permits', 1000, 1999, 0),
  costCodeCategory('cat-demolition', 'Demolition and removal', 2000, 2999, 1),
  costCodeCategory('cat-environmental', 'Environmental', 3000, 3999, 2),
  costCodeCategory('cat-tanks', 'Tanks and dispensers', 4000, 4999, 3),
  costCodeCategory('cat-sitework', 'Site work and paving', 5000, 5999, 4),
  costCodeCategory('cat-closeout', 'Testing and closeout', 6000, 6999, 5),
];

const costCode = (
  id: string,
  number: string,
  name: string,
  categoryId: string,
  costTypes: CostType[],
  markupPercent = 0,
): CostCode => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  number,
  name,
  categoryId,
  costTypes,
  // Through `markupFromPercent` rather than a literal, because the stored
  // unit is ten-thousandths of a percent and writing `200_000` by hand in a
  // fixture is how the field's doc comment came to be wrong by a hundred.
  defaultMarkupMillionths: markupFromPercent(markupPercent),
  active: true,
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
});

export const fixtureCostCodes: CostCode[] = [
  costCode('code-permits', '1010', 'Permits and fees', 'cat-preliminaries', ['other'], 0),
  costCode(
    'code-engineering',
    '1020',
    'Engineering and drawings',
    'cat-preliminaries',
    ['sub'],
    10,
  ),
  costCode('code-mobilisation', '1030', 'Mobilisation', 'cat-preliminaries', ['labor'], 15),
  costCode('code-pump-removal', '2010', 'Dispenser removal', 'cat-demolition', ['sub'], 15),
  costCode('code-tank-removal', '2020', 'Tank removal and disposal', 'cat-demolition', ['sub'], 15),
  costCode('code-concrete-demo', '2030', 'Concrete demolition', 'cat-demolition', ['sub'], 15),
  costCode(
    'code-soil-testing',
    '3010',
    'Soil sampling and testing',
    'cat-environmental',
    ['sub'],
    10,
  ),
  // The contamination branch of the critical path. Zero markup on purpose:
  // remediation is passed through at cost, which is a real convention and
  // the reason `defaultMarkupMillionths` exists per code rather than per
  // company.
  costCode('code-remediation', '3020', 'Soil remediation', 'cat-environmental', ['sub'], 0),
  costCode('code-monitoring', '3030', 'Monitoring wells', 'cat-environmental', ['sub'], 10),
  costCode('code-tanks', '4010', 'Tanks', 'cat-tanks', ['material'], 20),
  costCode('code-dispensers', '4020', 'Dispensers', 'cat-tanks', ['material'], 20),
  costCode('code-piping', '4030', 'Piping and sumps', 'cat-tanks', ['material', 'labor'], 20),
  costCode('code-tank-install', '4040', 'Tank setting and installation', 'cat-tanks', ['sub'], 15),
  costCode('code-backfill', '5010', 'Backfill and compaction', 'cat-sitework', ['sub'], 15),
  costCode('code-paving', '5020', 'Paving', 'cat-sitework', ['sub'], 15),
  costCode('code-striping', '5030', 'Striping and signage', 'cat-sitework', ['sub'], 20),
  costCode('code-tightness', '6010', 'Tightness testing', 'cat-closeout', ['sub'], 10),
  costCode('code-inspection', '6020', 'Inspection and sign-off', 'cat-closeout', ['other'], 0),
];

/**
 * A small catalog for the fuel-station demo (P5-03).
 *
 * Six items, chosen so the three markup states are all visible on one
 * screen: two inherit their cost code's default, one is explicitly at cost,
 * and one carries its own percentage. That distinction is the only
 * interesting thing on the page and a demo where every row looked the same
 * would not show it.
 */
const catalogItem = (
  id: string,
  title: string,
  costCodeId: string,
  cents: number,
  unit: string,
  over: Partial<CatalogItem> = {},
): CatalogItem => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  title,
  description: '',
  costCodeId,
  unitCost: money(cents),
  unit,
  active: true,
  // Most of a catalog is lines waiting to be used — a labour rate, a
  // subcontract price — and none of those sit on a shelf. The two below
  // that do are marked individually.
  stocked: false,
  tagIds: [],
  createdAt: AT,
  createdBy: BY,
  updatedAt: AT,
  updatedBy: BY,
  ...over,
});

export const fixtureCatalogItems: CatalogItem[] = [
  // Inherits 20% from `4010 Tanks`.
  catalogItem('cat-tank-12k', '12,000 gallon double-wall tank', 'code-tanks', 4_850_000, 'each', {
    description: 'Fibreglass, double-wall, with interstitial monitoring port.',
    costType: 'material',
  }),
  // Inherits 20% from `4020 Dispensers`.
  catalogItem(
    'cat-dispenser',
    'Four-hose dispenser with card reader',
    'code-dispensers',
    1_240_000,
    'each',
    {
      costType: 'material',
    },
  ),
  // **Explicitly at cost.** A permit fee is passed through, and if this
  // inherited the code's default the owner would be charged a markup on a
  // government fee.
  catalogItem('cat-adeq-permit', 'ADEQ tank installation permit', 'code-permits', 95_000, 'each', {
    description: 'Per tank. Passed through at cost.',
    costType: 'other',
    markupMillionths: 0,
  }),
  // Its own rate, lower than the code's default because it is a long job
  // at a negotiated price.
  catalogItem('cat-excavation', 'Excavation, tank pit', 'code-tank-removal', 18_500, 'cu yd', {
    costType: 'sub',
    markupMillionths: markupFromPercent(12),
  }),
  catalogItem('cat-backfill-sand', 'Pea gravel backfill', 'code-backfill', 4_200, 'ton', {
    stocked: true,
    sku: 'SKU-0001',
    costType: 'material',
  }),
  // The second stocked line, and deliberately a small consumable: the
  // thing stock is actually for is the box of connectors nobody can say
  // the number of, not the tank that arrives on its own lorry.
  // 0.48, which is what the **last** delivery cost — see
  // `fixtureStockMovements`. A stocked item's `unitCost` is the latest
  // price paid, so a fixture whose item and ledger disagreed would be
  // showing a state `receiveStock` cannot produce.
  catalogItem('cat-rj45', 'Cat5e RJ45 connector', 'code-piping', 48, 'each', {
    description: 'Shielded, pass-through. Bagged in hundreds.',
    stocked: true,
    sku: 'SKU-0002',
    costType: 'material',
  }),
  catalogItem(
    'cat-soil-sample',
    'Soil sample and lab analysis',
    'code-soil-testing',
    32_500,
    'each',
    {
      costType: 'sub',
    },
  ),
];

/**
 * One assembly, so the bundle path is exercised by every run.
 *
 * "Set one tank" is the real unit a fuel contractor thinks in: the tank,
 * its permit, the excavation and the backfill. Adding two of these to an
 * estimate should produce eight lines with the quantities doubled, which is
 * the thing `assemblyPicks` exists to get right.
 */
export const fixtureCatalogAssemblies: CatalogAssembly[] = [
  {
    id: 'asm-set-tank',
    companyId: FIXTURE_COMPANY_ID,
    title: 'Set one tank',
    description: 'Tank, permit, pit excavation and backfill for a single 12,000 gallon tank.',
    lines: [
      { catalogItemId: 'cat-tank-12k', quantityThousandths: 1_000 },
      { catalogItemId: 'cat-adeq-permit', quantityThousandths: 1_000 },
      { catalogItemId: 'cat-excavation', quantityThousandths: 42_000 },
      { catalogItemId: 'cat-backfill-sand', quantityThousandths: 36_000 },
    ],
    active: true,
    tagIds: [],
    createdAt: AT,
    createdBy: BY,
    updatedAt: AT,
    updatedBy: BY,
  },
];

/**
 * Deliveries, so the shelf is not empty (INV-05b).
 *
 * A level is **derived** — the trigger on Firebase, the mirror here —
 * so stock is seeded as the movements that produced it rather than as
 * the counts. Seeding counts directly would make the fixture the one
 * place in the product where a level is typed over, and the first thing
 * anybody did with it would be to wonder why the history did not add up.
 *
 * Two deliveries of the connectors at **different prices**, because
 * latest-price-paid is the decision this product took and a fixture with
 * one price per item never shows what that means: 1,000 at 0.42 then 500
 * at 0.48 is 660.00 spent and 720.00 held.
 */
const movement = (
  id: string,
  catalogItemId: string,
  kind: StockMovement['kind'],
  quantityThousandths: number,
  on: DateOnly,
  over: Partial<StockMovement> = {},
): StockMovement => ({
  id,
  companyId: FIXTURE_COMPANY_ID,
  catalogItemId,
  kind,
  quantityThousandths,
  on,
  note: '',
  at: `${on}T15:00:00.000Z`,
  by: BY,
  createdAt: `${on}T15:00:00.000Z`,
  createdBy: BY,
  updatedAt: `${on}T15:00:00.000Z`,
  updatedBy: BY,
  ...over,
});

export const fixtureStockMovements: StockMovement[] = [
  movement('mov-gravel-1', 'cat-backfill-sand', 'received', 120_000, '2026-02-10', {
    unitCost: money(4_200),
    reference: 'DN-88431',
  }),
  movement('mov-rj45-1', 'cat-rj45', 'received', 1_000_000, '2026-02-12', {
    unitCost: money(42),
    reference: 'DN-88470',
  }),
  movement('mov-rj45-2', 'cat-rj45', 'received', 500_000, '2026-03-02', {
    unitCost: money(48),
    reference: 'DN-89102',
  }),
  // A count that was wrong, corrected the only way history allows: an
  // opposing movement that says why. Nobody edits the ledger.
  movement('mov-rj45-3', 'cat-rj45', 'adjusted', -24_000, '2026-03-14', {
    note: 'Counted the shelf: a part bag went out on the Saguaro job unrecorded.',
  }),
];
